// src/services/schedulerService.ts
import { db, auth } from '@/lib/firebase';
import {
    collection,
    query,
    where,
    getDocs,
    addDoc,
    Timestamp,
    updateDoc,
    doc
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Bill } from '@/context/BillContext';
import { Invoice } from '@/context/InvoiceContext';
import { CompanySettings } from '@/context/CompanyContext';

export type DocumentSchedule = {
    id?: string;
    documentId: string;
    documentType: 'bill' | 'invoice';
    budgetId: string;
    scheduledDate: Date;
    scheduleType: 'reminder' | 'notice';
    status: 'pending' | 'processed' | 'failed';
    reminderNumber?: number;
    metadata?: {
        daysUntilDue?: number;
        daysOverdue?: number;
    };
    createdBy: string;
    createdAt: Date;
    updatedAt: Date;
};

export const schedulerService = {
    async scheduleReminders(
        document: Bill | Invoice,
        documentType: 'bill' | 'invoice',
        companySettings: CompanySettings
    ) {
        try {
            const userId = auth.currentUser?.uid;
            if (!userId) throw new Error('User not authenticated');
    
            // Calculate reminder dates based on due date
            const dueDate = new Date(document.dueDate);
            const now = new Date();
    
            // Schedule initial notice based on settings
            const noticeDays = documentType === 'bill'
                ? companySettings.billSettings?.noticeDays || 7
                : companySettings.invoiceSettings?.noticeDays || 7;
    
            const noticeDate = new Date(dueDate);
            noticeDate.setDate(noticeDate.getDate() - noticeDays);
    
            // Only schedule notice if due date hasn't passed yet
            if (dueDate > now && noticeDate > now) {
                await this.createSchedule({
                    documentId: document.id!,
                    documentType,
                    budgetId: document.budgetId!,
                    scheduledDate: noticeDate,
                    scheduleType: 'notice',
                    status: 'pending',
                    metadata: {
                        daysUntilDue: noticeDays
                    },
                    createdBy: userId,
                    createdAt: new Date(),
                    updatedAt: new Date()
                });
            }
    
            // Get reminder settings based on document type
            const reminderSettings = documentType === 'bill'
                ? companySettings.billSettings
                : companySettings.invoiceSettings;
    
            // Default reminder days if settings not available
            const reminderDays = reminderSettings?.reminderDays || [-3, -1, 0, 1, 3];
    
            // Calculate days overdue if the due date has passed
            const isOverdue = dueDate < now;
            const daysOverdue = isOverdue ? 
                Math.ceil((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)) : 0;
    
            for (let i = 0; i < reminderDays.length; i++) {
                const reminderDate = new Date(dueDate);
                reminderDate.setDate(reminderDate.getDate() + reminderDays[i]);
                
                // For overdue documents, schedule immediate reminders if within overdue period
                const scheduleDate = isOverdue && reminderDays[i] > 0 ? 
                    new Date(now.getTime() + (i * 24 * 60 * 60 * 1000)) : // Schedule 1 day apart for overdue
                    reminderDate;
    
                // For overdue documents, schedule if reminder day is positive (overdue reminder)
                // For non-overdue documents, schedule if reminder date is in the future
                if ((!isOverdue && reminderDate > now) || (isOverdue && reminderDays[i] > 0)) {
                    const metadata = isOverdue ? {
                        daysOverdue: daysOverdue + (i * 1) // Increment overdue days for each reminder
                    } : {
                        daysUntilDue: reminderDays[i] < 0 ? Math.abs(reminderDays[i]) : undefined,
                        daysOverdue: reminderDays[i] > 0 ? reminderDays[i] : undefined
                    };
    
                    await this.createSchedule({
                        documentId: document.id!,
                        documentType,
                        budgetId: document.budgetId!,
                        scheduledDate: scheduleDate,
                        scheduleType: 'reminder',
                        status: 'pending',
                        reminderNumber: i + 1,
                        metadata,
                        createdBy: userId,
                        createdAt: new Date(),
                        updatedAt: new Date()
                    });
                }
            }
    
            // Update document with schedule status and overdue status if applicable
            const collectionName = documentType === 'bill' ? 'bills' : 'invoices';
            const docRef = doc(db, collectionName, document.id!);
            await updateDoc(docRef, {
                reminderScheduled: true,
                status: isOverdue ? 'overdue' : document.status,
                daysOverdue: isOverdue ? daysOverdue : null,
                updatedAt: Timestamp.now()
            });
    
            return true;
        } catch (error) {
            console.error('Error scheduling reminders:', error);
            throw error;
        }
    },

    async createSchedule(schedule: Omit<DocumentSchedule, 'id'>) {
        try {
            // Clean up metadata before sending to Firestore
            const cleanMetadata = {
                daysUntilDue: schedule.metadata?.daysUntilDue || null,
                daysOverdue: schedule.metadata?.daysOverdue || null
            };

            const cleanSchedule = {
                ...schedule,
                metadata: cleanMetadata,
                scheduledDate: Timestamp.fromDate(schedule.scheduledDate),
                createdAt: Timestamp.fromDate(schedule.createdAt),
                updatedAt: Timestamp.fromDate(schedule.updatedAt)
            };

            await addDoc(collection(db, 'document_schedules'), cleanSchedule);
        } catch (error) {
            console.error('Error creating schedule:', error);
            throw error;
        }
    },

    async triggerScheduledReminders() {
        const functions = getFunctions();
        const processReminders = httpsCallable(functions, 'processDocumentReminders');

        try {
            await processReminders();
        } catch (error) {
            console.error('Error triggering reminders:', error);
            throw error;
        }
    },

    async getScheduledReminders(
        documentId: string,
        documentType: 'bill' | 'invoice'
    ): Promise<DocumentSchedule[]> {
        try {
            const userId = auth.currentUser?.uid;
            if (!userId) throw new Error('User not authenticated');

            const q = query(
                collection(db, 'document_schedules'),
                where('documentId', '==', documentId),
                where('documentType', '==', documentType),
                where('createdBy', '==', userId)
            );

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                scheduledDate: doc.data().scheduledDate.toDate(),
                createdAt: doc.data().createdAt.toDate(),
                updatedAt: doc.data().updatedAt.toDate()
            })) as DocumentSchedule[];
        } catch (error) {
            console.error('Error getting scheduled reminders:', error);
            throw error;
        }
    },

    async checkPendingReminders() {
        try {
            const userId = auth.currentUser?.uid;
            if (!userId) throw new Error('User not authenticated');

            const now = new Date();
            const q = query(
                collection(db, 'document_schedules'),
                where('status', '==', 'pending'),
                where('scheduledDate', '<=', now),
                where('createdBy', '==', userId)
            );

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                scheduledDate: doc.data().scheduledDate.toDate(),
                createdAt: doc.data().createdAt.toDate(),
                updatedAt: doc.data().updatedAt.toDate()
            })) as DocumentSchedule[];
        } catch (error) {
            console.error('Error checking pending reminders:', error);
            throw error;
        }
    }
};

export default schedulerService;